import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Typography, Box, Link, Chip } from '@mui/material'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import CloseIcon from '@mui/icons-material/Close'
import { useDrag } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { deliveryOrderStatuses } from '../../lib/statuses'
import { formatDeliveryTimeRange, getDate } from '../../lib/utils'

const ItemTypes = { ORDER: 'order' }

const resolveSite = (siteBdn, site, address, city) => {
  if (siteBdn?.address) return siteBdn.address
  if (site?.address) return [site.address, site.city].filter(Boolean).join(' ')
  if (address) return [address, city].filter(Boolean).join(' ')
  return null
}

const OrderCardFullScreen = ({ deliveryOrder, fromDriverId, isDragDisabled, selected, onToggleSelect, isPopup }) => {
  const { t } = useTranslation('planning_page')
  const [showMore, setShowMore] = useState(false)

  const [{ isDragging }, ref] = useDrag({
    type: ItemTypes.ORDER,
    item: { id: deliveryOrder.id, name: deliveryOrder.name, fromDriverId: fromDriverId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const statusEntry = deliveryOrderStatuses().find((s) => s.value === deliveryOrder.status)
  const statusLabel = statusEntry ? t(deliveryOrder.status) : deliveryOrder.status

  const depart = resolveSite(
    deliveryOrder.startingSiteBdn,
    deliveryOrder.startingSite,
    deliveryOrder.startingSiteAddress,
    deliveryOrder.startingSiteCity
  )
  const livraison = resolveSite(
    deliveryOrder.deliverySiteBdn,
    deliveryOrder.deliverySite,
    deliveryOrder.deliverySiteAddress,
    deliveryOrder.deliverySiteCity
  )
  const dechargement = resolveSite(
    deliveryOrder.unloadingSiteBdn,
    deliveryOrder.unloadingSite,
    deliveryOrder.unloadingSiteAddress,
    deliveryOrder.unloadingSiteCity
  )

  return (
    <Card
      ref={ref}
      sx={isPopup ? {
        display: 'flex',
        width: '100%',
        padding: '12px',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        gap: '8px',
        borderRadius: '4px',
        border: '1px solid #1577FC',
        background: 'rgba(204, 225, 255, 0.30)',
        opacity: isDragging ? 0.5 : 1,
      } : { width: '100%', opacity: isDragging ? 0.5 : 1, cursor: isDragDisabled ? 'default' : 'grab' }}
    >
      <CardContent sx={{ pb: '12px !important', ...isPopup ? {width: '100%'} : {} }}>
        {/* Row 1: close icon + client name + status chip */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {isPopup ? (
              selected && (
                <Box
                  sx={{ display: 'flex', cursor: 'pointer' }}
                  onPointerDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
                  onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); onToggleSelect(deliveryOrder.id); }}
                >
                  <CloseIcon
                    sx={{ color: '#737373', fontSize: 20, position: 'static', pointerEvents: 'none' }}
                  />
                </Box>
              )
            ) : (
              <Box
                sx={{ display: 'flex', cursor: 'pointer' }}
                onPointerDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
                onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); onToggleSelect(deliveryOrder.id); }}
              >
                {selected ? (
                  <RadioButtonCheckedIcon sx={{ color: '#1577FC', fontSize: 20, flexShrink: 0, position: 'static', pointerEvents: 'none' }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: '#737373', fontSize: 20, flexShrink: 0, position: 'static', pointerEvents: 'none' }} />
                )}
              </Box>
            )}
            <Typography fontWeight="bold" variant="body1">
              {deliveryOrder.command.clientName}
            </Typography>
          </Box>
          {statusEntry && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                backgroundColor: statusEntry.backgroundColor,
                color: statusEntry.color,
                fontWeight: 500,
                ml: 1,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        {/* Row 2: Date + Horaire */}
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="body2">
            <strong>{t('date')} :</strong> {getDate(deliveryOrder.deliveryDate)}
          </Typography>
          <Typography variant="body2">
            <strong>{t('schedule')} :</strong>{' '}
            {formatDeliveryTimeRange(deliveryOrder.deliveryTimeStart, deliveryOrder.deliveryTimeEnd)}
          </Typography>
        </Box>

        {/* Sites */}
        {(depart || livraison || dechargement) && (
          <Box mt={3}>
            <Typography variant="body2" fontWeight="bold">
              {t('sites')} :
            </Typography>
            {depart && (
              <Typography variant="body2" noWrap>
                {t('depart')} → {depart}
              </Typography>
            )}
            {livraison && (
              <Typography variant="body2" noWrap>
                {t('delivery')} → {livraison}
              </Typography>
            )}
            {dechargement && (
              <Typography variant="body2" noWrap>
                {t('unloading')} → {dechargement}
              </Typography>
            )}
          </Box>
        )}

        {/* Commande & ODL */}
        <Box mt={2}>
          <Typography variant="body2">
            <strong>{t('orderNumber')} :</strong> {deliveryOrder.command.commandNumber} - {deliveryOrder.orderDeliveryNumber}
          </Typography>
        </Box>

        {/* Voir plus */}
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Link onClick={() => setShowMore((v) => !v)} sx={{ cursor: 'pointer' }}>
            {showMore ? t('seeLess') : t('seeMore')}
          </Link>
        </Box>

        {showMore && (
          <Box mt={1}>
            <Typography variant="body2">{deliveryOrder.command.contactPhone}</Typography>
            <Typography variant="body2">{deliveryOrder.command.comment}</Typography>
            {deliveryOrder.orderDeliveryMaterials?.nodes?.length > 0 && (
              <Box mt={1}>
                <Typography variant="body2" fontWeight="bold">{t('products')} :</Typography>
                {deliveryOrder.orderDeliveryMaterials.nodes.map((material, index) => (
                  <Typography key={index} variant="body2">
                    {material.material.description}: {material.quantity}{' '}
                    {material.material.measureUnitByBaseMeasureUnitCode.abbr}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

OrderCardFullScreen.propTypes = {
  deliveryOrder: PropTypes.any.isRequired,
  isDragDisabled: PropTypes.bool,
  fromDriverId: PropTypes.string,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  isPopup: PropTypes.bool,
}

export default OrderCardFullScreen
